import { type MutableRefObject, useRef } from 'react';
import { usePdpSharedImageGalleryModalState } from './usePdpSharedImageGalleryModalState.ts';

type GalleryModalController = ReturnType<typeof usePdpSharedImageGalleryModalState> & {
    selectedIndexRef: MutableRefObject<number>;
    openGalleryModal: (index: number) => void;
    handleCloseGrid: () => void;
};

/**
 * Wraps the raw gallery modal open/close state with the open + close-and-restore
 * behavior shared by every carousel that mounts the gallery. Each surface only
 * differs in how it restores the underlying carousel position on close, supplied
 * via `onRestoreIndex`.
 */
export const usePdpSharedGalleryModalController = ({
    disableModals = false,
    onRestoreIndex,
}: {
    disableModals?: boolean;
    onRestoreIndex: (index: number) => void;
}): GalleryModalController => {
    const state = usePdpSharedImageGalleryModalState();
    const { selectedIndex, openGrid, closeGrid } = state;

    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;

    const openGalleryModal = (index: number): void => {
        if (disableModals) {
            return;
        }
        openGrid(index);
    };

    const onRestoreIndexRef = useRef(onRestoreIndex);
    onRestoreIndexRef.current = onRestoreIndex;

    const handleCloseGrid = (): void => {
        const restoreIndex = selectedIndexRef.current;
        closeGrid();
        setTimeout(() => {
            onRestoreIndexRef.current(restoreIndex);
        }, 50);
    };

    return {
        ...state,
        selectedIndexRef,
        openGalleryModal,
        handleCloseGrid,
    };
};
